import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { PageHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { SortableTh, Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import useSortState from '@/hooks/useSortState';
import { DepartmentResource } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

import DepartmentFormModal from './components/DepartmentFormModal';
import DepartmentsTableRow from './components/DepartmentsTableRow';

const StyledTable = styled(Table)`
  td,
  th {
    vertical-align: middle;
    font-weight: 700;
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      width: 64px;
      padding-right: 24px;
    }
  }
`;

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const Departments = () => {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);

  const { sort, setSort, sortQueryParamObj } = useSortState({
    property: 'hide_from_dropdowns',
    direction: SortDirection.ASC,
  });

  const debouncedSearch = useDebounce(search, 300);

  const {
    data: departments,
    isError,
    isLoading,
  } = useQuery(
    [
      'departments_and_units',
      {
        sort,
        search: debouncedSearch,
      },
    ],
    () =>
      api.get<never, DepartmentResource[]>('/v1/departments', {
        params: {
          ...(!!search && {
            'filter[name]': debouncedSearch,
          }),
          ...sortQueryParamObj,
        },
      }),
    {
      staleTime: 90 * 1000,
    },
  );

  return (
    <>
      <HeadingRow>
        <PageHeading>Departments</PageHeading>
        <Button onClick={() => setShowModal(true)}>Add New</Button>
      </HeadingRow>
      <SearchBar value={search} setValue={setSearch} />
      <ScrollableTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="name"
                title="Name"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="short"
                title="Short"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="company_name"
                title="Company"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="hide_from_dropdowns"
                title="Hide from dropdowns"
              />
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!departments?.length}
              colSpan={5}
            >
              {departments?.map((department) => (
                <DepartmentsTableRow
                  key={department.id}
                  department={department}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <AnimatePresence>
        {showModal && (
          <DepartmentFormModal
            onClose={() => {
              setShowModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Departments;
